<script setup>
useHead({ bodyAttrs: { id: 'public' } })
const route   = useRoute()
const router  = useRouter()
const points  = useState('points')
const account = useState('account')
const settings = useState('settings')

const {site}  = settings.value || {}

const showDrawer = ref(false)
const dialog = reactive({
  account: false,
  pointHistory: false,
  purchasePoint: false,
  favorite: false,
  search: false,
})

const hideMenu = computed(() => !(
  route.path.includes('/login')   ||
  route.path.includes('/regist')  ||
  route.path.includes('/recover')
))
</script>

<template>
  <header>
    <div class="header-wrapper">
      <div class="header-left">
        <div class="hidden xl:block">
          <icon icon="fa6-solid:user" width="18" class="mb-1 mr-1" />
          <div class="border-b border-zinc px-1 pb-1 inline-flex items-end gap-x-3">
            <span>
              <span v-if="!account">ようこそ</span>
              <span v-if="account" class="text-lg">{{ account?.last_name }} {{ account?.first_name }}</span>
              <span v-else class="text-lg">ゲスト</span>
              さん
            </span>

            <div class="hidden lg:block">
              <button
                v-if="account"
                type="button"
                class="btn btn-xs btn-stone"
                @click="dialog.account = true"
              >会員情報</button>
              <NuxtLink
                v-else
                to="/login"
                class="btn btn-xs btn-primary"
              >ログイン</NuxtLink>
            </div>

            <template v-if="account && hideMenu">
              <div>|</div>

              <button
                v-if="!isNaN(points)"
                type="button"
                class="block"
                @click="dialog.pointHistory = true"
              >
                <small class="w-6 h-6 font-bold inline-flex items-center justify-center rounded-full border-4 border-double border-zinc p-1 mr-1">P</small>
                <strong>{{ points.toLocaleString() }}<small class="font-bold">ポイント</small></strong>
              </button>
            </template>

            <NuxtLink
              to="/beginner"
              class="hidden xl:inline-flex items-end gap-x-1"
            >
              <NuxtImg src="/icon/beginner.png" width="16px" class="inline-block" />
              <span class="pb-0.5">初めての方へ</span>
            </NuxtLink>
          </div>
        </div>

        <h1 class="absolute -top-[9999px]">{{ site.name || '/logo.png' }}</h1>
      </div>

      <NuxtLink to="/" class="header-logo">
        <NuxtImg :src="site.logo" />
      </NuxtLink>

      <div class="header-right">
        <template v-if="hideMenu">
          <div v-if="account" class="hidden sm:block xl:hidden">
            <button
              type="button"
              class="btn text-sm !min-w-14 !font-noto-sans"
              @click="dialog.account = true"
            ><icon icon="mdi:account" width="30" /></button>
          </div>

          <div class="hidden sm:block">
            <button
              id="btn-purchase-point"
              type="button"
              class="btn text-sm !min-w-14 !font-noto-sans"
              @click="dialog.purchasePoint = true"
            >
              <NuxtImg src="/icon/coins-lg.png" width="35px" />
              <span class="hidden xl:inline text-left leading-tight">ポイントを<br>購入する</span>
            </button>
          </div>

          <div class="hidden sm:block">
            <button
              type="button"
              class="btn text-sm !min-w-14 !font-noto-sans"
              @click="account ? dialog.favorite = true : router.push('/login')"
            >
              <NuxtImg src="/icon/town.png" class="h-6 md:h-8" />
              <span class="hidden xl:inline">お気に入り店舗</span>
            </button>
          </div>

          <default-ui-header-dialog-search />
        </template>

        <button
          type="button"
          class="btn btn-circle"
          @click="showDrawer = true"
        >
          <icon icon="mdi:menu" width="28" />
        </button>
      </div>
    </div>

    <default-ui-header-drawer v-model="showDrawer" />

  </header>

  <div class="overflow-x-hidden" style="width:stretch">
    <slot></slot>
  </div>

  <footer>
  </footer>

  <client-only >
    <default-ui-lightbox />
  </client-only>

  <div
    v-if="hideMenu"
    class="sticky bottom-0 z-[998] w-full flex sm:hidden border-t divide-x overflow-hidden bg-white"
    :style="{boxShadow: '0 7px 7px 2px #333'}"
  >
    <button
      v-if="account"
      class="flex-1 flex flex-col text-center py-1 bg-white active:bg-primary/5"
      @click="dialog.account = true"
    >
      <icon icon="mdi:account" width="35" class="mx-auto text-zinc" />
      <small class="block w-full mt-auto text-center text-secondary font-noto-serif">会員情報</small>
    </button>
    <NuxtLink
      v-else
      to="/login"
      class="flex-1 flex flex-col text-center py-1 bg-white active:bg-primary/5 !no-underline"
    >
      <icon icon="mdi:login" width="35" class="mx-auto text-primary" />
      <small class="block w-full mt-auto text-center text-secondary font-noto-serif">ログイン</small>
    </NuxtLink>

    <button
      class="flex-1 flex flex-col text-center py-1 bg-white active:bg-primary/5"
      @click="dialog.purchasePoint = true"
    >
      <NuxtImg src="/icon/coins-lg.png" height="35px" class="mx-auto" />
      <small class="block w-full mt-auto text-center text-secondary font-noto-serif">ポイントを購入</small>
    </button>
    <button
      class="flex-1 flex flex-col text-center py-1 bg-white active:bg-primary/5"
      @click="account ? dialog.favorite = true : router.push('/login')"
    >
      <NuxtImg src="/icon/town.png" height="35px" class="mx-auto" />
      <small class="block w-full mt-auto text-center text-secondary font-noto-serif">お気に入り店舗</small>
    </button>
  </div>

  <default-ui-header-dialog-account-info v-if="account" v-model="dialog.account" />
  <default-ui-header-dialog-point-history v-if="account" v-model="dialog.pointHistory" />
  <default-ui-header-dialog-purchase-point v-model="dialog.purchasePoint" />
  <default-ui-header-dialog-favorite v-if="account" v-model="dialog.favorite" />

  <default-ui-loading />
  <default-ui-notification />
</template>